import _ from 'lodash';
import { createMuiTheme } from '@material-ui/core/styles';
import { theme as acmeTheme } from 'themes/themeAcme';
import { theme as quickenTheme } from 'themes/themeQuicken';
import { greyScale, applyOpacityToHex, colorScale } from 'themes/defaultColors';
import { getThemeOverrideByKey } from 'themes/allThemeOverrides';
import createCachedSelector, { LruCacheObject } from 're-reselect';
import { isAcme } from 'isAcme';

const blue1 = '#295e9b';
const blue2 = '#3e6da3';
const blue3 = '#4f89cb';
const blue4 = '#5cb3ea';
const blue5 = '#9bcbeb';

const green1 = '#008e38';
const green2 = '#00b045';
const green3 = '#5ac402';

const red1 = '#d70015';
const red2 = '#e12b3d';
const red3 = '#db4d48';

const teal2 = '#088391';

const gray2 = '#404040';
const gray3 = '#474F57';
const gray5 = '#c8c8c8';

const gold = '#eaa844';

const status = {
  lowLight: '#67de6f',
  lowMain: '#34ab3c',
  lowDark: '#118819',
  mediumLight: '#ffd444',
  mediumMain: '#eaa111',
  mediumDark: '#c67d00',
  highLight: '#ff6e6e',
  highMain: '#dc3b3b',
  highDark: '#b91818',
};

// This is the base theme for the app, it's the same for Quicken and ACME
const muiThemeBase = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,     // default was 960
      lg: 1280,
      xl: 1920,
    },
  },
  featureHeader: {
    titleVariant: 'h6',
    subTitleVariant: 'subtitle1',
  },
  typography: {
    useNextVariants: true,
    caption: {
      letterSpacing: 'normal',
      color: greyScale[1],
    },
    overline: {
      lineHeight:1.66,
    },
    subtitle1: {
      fontWeight: 500,
    },
    h5:{
      fontWeight: 500,
    },
  },
  spacing: 8,
  overrides: {
    MuiInput: {
      input: {
        letterSpacing: '0.01071em',
      },
    },
    MuiDialogTitle: {
      root: {
        padding: 24,
      },
    },
    MuiDialogContent: {
      root: {
        padding: 24,
      },
    },
    MuiDialogActions: {
      spacing: {
        padding: 24,
      },
    },
  },
  defaults: {
    dense: window.screen.width > 1024,
    buttonHoverBackgroundColor: '#ebebeb',
    content: {
      // -- sizing -- //
      height: '100%',
      width: 960,
      pageMinWidth: 690,
      // -- spacing -- //
      paddingLeft: 32,
      paddingLeftMobile: 32,
      paddingRight: 32,
      paddingRightMobile: 32,
    },
    headers: {
      // -- sizing -- //
      subHeaderHeight: isAcme ? 75 : 54,
      subHeaderHeightTall: isAcme ? 75 : 70,
      headerHeight: isAcme ? 75 : 70,
    },

  },
  palette: {  // this should be merged with themeAcme || themeQuicken
    linkColor: '#4a90e2',
    background: {
      default: '#ffffff',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    greyScale,
    status,
    primary: {
      main: '#1f74ba',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#666666',
      contrastText: '#ffffff',
    },
    // -- TEMPORARY -- //
    positiveLight: '#ADD7AD',

    blue1,
    blue2,
    blue3,
    blue4,
    blue5,

    green1,
    green2,
    green3,

    red1,
    red2,
    red3,

    teal2,

    gray2,
    gray3,
    gray5,

    gold,
  },
  // - 'components' is a section for components in the app that have set styles we want stored in the theme
  // - only add new sections here if it's necessary to be stored in a central point (the theme) or if the style
  //    depends on if it's Acme or Quicken, otherwise these styles can be handled in the component files
  components: {
    register: {
      // -- spacing -- //
      paddingLeft: 20,
      paddingRight: 20,
      rowInnerPadding: 6,
      fieldPadding: 1,
      projectedSmallOffset: 116, // subHeaderHeightTall + regProjectedHeightClosed,
      projectedBigOffset: 396,  // subHeaderHeightTall + regProjectedHeightOpen,

      // -- typography -- //
      fontSize: {
        small: '12px',
        default: '14px',
        large: '16px',
      },
      // -- sizing -- //
      projectedHeightClosed: 52,
      projectedHeightOpen: 332,
      splitRowHeight: 45,
      checkboxSize: 20,
      controlBarHeight: 60,
      sectionHeaderHeight: 34,
      // -- options -- //
      noUnderline: true,

      // -- COLORS -- //
      fieldBorderColor: '#4a90e2',
      splitsBackground: 'rgba(0, 100, 171, 0.03)',
      headerBackground: '#fafaf9',
      headerBorder: 'rgba(201, 199, 197, 0.5)',
      editRowColor: '#fafaf9',
      catTagDropdown: 'rgba(0, 100, 171, 0.08)',
      sectionHeaderColor: 'rgba(236, 235, 234, 0.3)',
      sectionHeaderBorder: 'rgba(201, 199, 197, 0.3)',
      rowHoverColor: '#fafaf9',
      fieldHoverColor: 'rgba(0, 0, 0, 0.2)',
    },
    accountCard: {
      fiWarningBorder: '#ffe4a0',
      fiWarningBackground: '#fff9e9',
      shadowColor: 'rgba(62, 62, 60, 0.1)',

      statusColor: 'rgba(0, 0, 0, 0.6)',
      changeIndicator: 'rgba(220,230,235, 0.5)',
      settingsActionColor: '#ecebea',
    },
    accountDrawer: {
      // -- spacing -- //
      width: 280,
      accountListLevelIndent: 4,
      accountListBaseFontSize: 16,
      drawerListTitleFontSize: 20,
      titleTransform: 'uppercase',
      titleWeight: 500,

      basePadding: 24,
      listPaddingLeft: 24,
      listPaddingRight: 24,
      iconSize: 20,

      // -- sizing -- //
      collapsePoint: 1150,
      collapsedWidth: 16,

      // -- Text colors -- //
      positiveBalance: null,
      highlightText: greyScale[7], // total + net-worth
      textColor: greyScale[7], // banking + investments
      headerListText: greyScale[7], // accounts bar
      l2TextColor: applyOpacityToHex(greyScale[7], 0.93),
      l3TextColor: applyOpacityToHex(greyScale[7], 0.88),
      negativeBalance: '#ffb7b5',

      // -- Background colors -- //
      background: '#1f6bb1',
      highlightColor: null, // total + net-worth
      hoverColor: null, // hover effect
      l1Color: null, // banking + investments
      logoBackground: '#678fe81a',
      headerBackground: '#1f74ba',

      // -- animation --//
      accountDrawerAnimation: 'all 500ms cubic-bezier(0.550, 0.085, 0.680, 0.530)',

      // other colors
      borderColor: null, // right-side border
      divider: '#1e69ac', // border of collapse button
      openControl: '#fafaf9',  // background inside collapse button,
      icon: '#1f74ba',  // icon inside collapse button,
      actionColor: greyScale[7], // add-FI button color
      // actionBackground: colorScale.others.link, // add-FI button background
      actionButtonSize: 40,

      // row sizing
      l1Margin: 0,
      l0Height: 40,
      l1Height: 40,
      l2Height: 40,
      l3Height: 32,

      addFiLabel: null,
    },
    navigation: {
      // -- sizing -- //
      barWidth: 56,
      iconSize: 24,
      // -- spacing -- //
      iconVerticalSpacing: 16,

      // -- animation -- //
      selectedIconAnimation: 'all 200ms cubic-bezier(0.550, 0.085, 0.680, 0.530)',
    },
    watchlist: {
      // -- sizing -- //
      spendingCardWidth: 380,
      spendingCardHeight: 284,
      spendingBarWidth: 48,
      spendingBarWidthSelected: 60,
    },
    amountField: {
      positiveColor: '#116611',
      negativeColor: '#d70015',
      negativeColor2: '#f5a623',
      positiveColor2: '#04a769',
      extremelyNegative: '#300', // Never used?
    },
    projectedBalances: {
      positiveGradientDark: '#bfd9fc',
      positiveGradientLight: 'rgba(245, 252, 254, 0)',
      todayBar: '#81929b',
      // negativeGradientDark: 'rgba(218, 101, 97, 0.8)',
      // negativeGradientLight: 'rgba(252, 223, 116, 0)',
    },
    categoryRegister: {
      labelHover: 'rgba(0, 0, 0, 0.2)',
      icon: 'rgba(0, 0, 0, 0.5)',
      collapseFilter: 'none',
    },
    spendingPlan: {
      // background color
      background: '#fbfbfb',
      // Expansion colors
      incomeColor: colorScale.color2.opacity100,
      billsColor: colorScale.color6.opacity100,
      goalsColor: colorScale.color9.opacity100,
      // Graph Colors
      planColor: colorScale.grey.level1,
      spentColor: colorScale.color1.opacity100,
      leftColor: colorScale.color3.opacity100,
      dangerColor: colorScale.color4.opacity100,
    },
    reports: {
      graphHeight: 324,
    },
    categoryCard: {
      barChartIconColor: greyScale[3],
      tabBackgroundSelected: greyScale[3],
      tabBackground: colorScale.color1.opacity70,
      tabTextColorSelected: greyScale[1],
      tabTextColor: 'white',
    },
    tagsField: {
      hoverColor: '#e5e5e5',
      clearIconColor: '#aaa',
    },
    currencySelect: {
      underlineColor: '#d8d8d8',
    },
    addAndBrowserDocuments: {
      deleteIconColor: '#c7939b',
    },
    payeeField: {
      shadowColor: '#ddd',
      displayTemplate: '#777',
    },
  },
  applyOpacityToHex,
};

const muiTheme = isAcme ? acmeTheme : quickenTheme;

// don't use this method directly ever
export const getThemeByKey = createCachedSelector(
  (themeKey) => themeKey || '',
  (themeKey) => createMuiTheme(_.mergeWith(  // deep merge of objects
    {},               // destination object for merge
    muiThemeBase,     // base theme consistent across the web app
    muiTheme,         // ACME or Quicken specific theme
    getThemeOverrideByKey(themeKey), // applying custom theme if there is one
    mergeCustomizer,  // ensures arrays are overridden in the merge instead of concatenated
  )),
)(
  (themeKey) => themeKey || '',
  {
    cacheObject: new LruCacheObject({ cacheSize: 5 }),
  },
);

// This function ensures that arrays are overridden in the merge instead of concatenated
//    - might be unnecessary but left here just in case
function mergeCustomizer(objValue, srcValue) {
  if (_.isArray(objValue)) {
    return srcValue;
  }
  return undefined; // lets lodash handle the merge
}
